import styles from './ScenarioMessageStyles.module.css';
import type { ScenarioMessage as ScenarioMessageType } from '../../api/types/scenario';
import { useEffect, useState, useRef } from 'react';
import ScenarioEditMessagePopUp from './ScenarioEditMessagePopUp';
import ScenarioEditMessagePopUpMobile from './ScenarioEditMessagePopUpMobile';
import ScenarioEditMessageFormMobile from './ScenarioEditMessageFormMobile';
import ScenarioInPlaceEdit from './ScenarioInPlaceEdit';
import AnimatedText from './AnimatedText';
import { useNavigate } from 'react-router-dom';

// Three dots component that appears on hover (desktop) or after tap (mobile)
function HoverDots({ isPersona, onButtonClick, isMobile }: { isPersona?: boolean, onButtonClick: (e: React.MouseEvent) => void, isMobile?: boolean }): JSX.Element {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up
    onButtonClick(e);
  };

  return (
    <button 
      className={`${styles.hoverDots} ${isPersona ? styles.personaHoverDots : ''} ${isMobile ? styles.mobileDots : ''}`}
      aria-label="Message options"
      type="button"
      onClick={handleClick}
    >
      <span></span>
      <span></span>
      <span></span>
    </button>
  );
}

// Loading message component with three-dot animation for characters
function LoadingMessage({ character }: { character: any }): JSX.Element {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Check for mobile view on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const containerClass = `${styles.messageContainer} ${isMobile ? styles.mobileMessage : ''} ${styles.loadingMessage}`;

  return (
    <div className={containerClass}>
      {character && character.icon && (
        <div className={styles.portraitContainer}>
          <img src={character.icon} alt={character.display_name} className={styles.portrait} />
          <div className={styles.speakerName}>{character.display_name}</div>
          <div className={styles.typingIndicator}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      )}
      <div className={styles.dialogueWrapper}>
        {/* Empty dialogue wrapper to maintain layout */}
      </div>
    </div>
  );
}

// Loading message component for narrator
function NarratorLoadingMessage(): JSX.Element {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Check for mobile view on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className={`${styles.narratorMessage} ${isMobile ? styles.mobileNarratorMessage : ''} ${styles.loadingMessage}`}>
      <div className={styles.narratorContent}>
        <div className={styles.typingIndicator}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  );
}

function ScenarioMessage({ 
  message, 
  isLoading, 
  isLatestMessage,
  onRegenerate,
  isRegenerating,
  onEditMessage,
  onRewindToMessage,
  onNewScenarioFromMessage,
  isRewinding,
  index = 0,
  isInitialLoad = false,
  onAnimationComplete
}: { 
  message: ScenarioMessageType, 
  isLoading?: boolean, 
  isLatestMessage?: boolean,
  onRegenerate?: (messageId: string) => Promise<void>,
  isRegenerating?: boolean,
  onEditMessage?: (messageId: string, content: string) => Promise<void>,
  onRewindToMessage?: (messageId: string) => Promise<void>,
  onNewScenarioFromMessage?: (messageId: string) => Promise<void>,
  isRewinding?: boolean,
  index?: number,
  isInitialLoad?: boolean,
  onAnimationComplete?: () => void
}): JSX.Element {
  const isUser = message.member_type === 'persona';
  const isNarrator = message.member_type === 'narrator';
  const speaker = isUser ? message.persona : message.character;
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isVisible, setIsVisible] = useState(!isInitialLoad);
  const [showPopup, setShowPopup] = useState(false);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [isSelected, setIsSelected] = useState(false);
  const [textAnimationComplete, setTextAnimationComplete] = useState(isInitialLoad);
  const navigate = useNavigate();
  
  // Add a state for the mobile edit form
  const [showMobileEditForm, setShowMobileEditForm] = useState(false);
  
  // Check for mobile view on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // Close popup if we resize to mobile
      if (window.innerWidth <= 768) {
        setShowPopup(false);
      }
      // Reset selected state when resizing
      setIsSelected(false);
      // Cancel edit mode when resizing
      if (isEditing) {
        setIsEditing(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isEditing]);

  // Add a click handler to the document to deselect messages when clicking elsewhere
  useEffect(() => {
    const handleDocumentClick = (e: MouseEvent) => {
      // Only handle this in mobile view
      if (isMobile && isSelected) {
        // Check if the click is outside the current message
        const messageElement = document.getElementById(`message-${message.id}`);
        if (messageElement && !messageElement.contains(e.target as Node)) {
          setIsSelected(false);
        }
      }
    };
    
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, [isMobile, isSelected, message.id]);

  // Animation effect when message appears
  useEffect(() => {
    if (isInitialLoad) {
      // For initial load messages, add a staggered delay based on index
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 300 + (index * 400)); // 300ms base delay + 400ms per message
      
      return () => clearTimeout(timer);
    } else {
      // For new messages during conversation, use a small delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [isInitialLoad, index]);
  
  // Handle tap for mobile
  const handleTap = () => {
    // Don't handle taps when in edit mode
    if (isEditing) return;
    
    if (isMobile) {
      // Toggle selected state on tap
      setIsSelected(prev => !prev);
    }
  };

  // Format the content with quotes and narrative text
  const formatContent = (content: string): string => {
    let formattedContent = content;
    
    // Handle quoted text first - for both character and narrator messages
    formattedContent = formattedContent.replace(/"([^"]+)"/g, '<q>$1</q>');
    
    // For single quotes, we need a more careful approach to avoid affecting contractions and possessives
    
    // Step 1: Temporarily mark all apostrophes that are likely part of contractions or possessives
    // This includes common patterns like 's, 've, 're, 'll, 'd, 'm, n't
    formattedContent = formattedContent.replace(/(\w)\'(s|ve|re|ll|d|m|t)\b/gi, '$1___APOSTROPHE___$2');
    formattedContent = formattedContent.replace(/n\'t\b/gi, 'n___APOSTROPHE___t');
    
    // Step 2: Also mark apostrophes in known common contractions that might not fit the pattern above
    const irregularContractions = [
      "ain't", "y'all", "o'clock", "ma'am", "o'er", 
      "e'er", "d'you", "c'mon", "y'know", "'em", "'cause"
    ];
    
    irregularContractions.forEach(contraction => {
      const escapedContraction = contraction.replace(/'/g, "\\'");
      const pattern = new RegExp(escapedContraction, 'gi');
      formattedContent = formattedContent.replace(pattern, match => {
        return match.replace("'", "___APOSTROPHE___");
      });
    });
    
    // Step 3: Now handle actual quoted text with single quotes
    // Look for patterns like: space/start + ' + text + ' + space/end/punctuation
    formattedContent = formattedContent.replace(/(\s|^)'([^']+)'(\s|$|[.,!?;:])/g, '$1<q>$2</q>$3');
    
    // Step 4: Restore all apostrophes
    formattedContent = formattedContent.replace(/___APOSTROPHE___/g, "'");
    
    if (isUser) {
      // Handle text between ** for emphasis/actions
      formattedContent = formattedContent.replace(/\*\*([^*]+)\*\*/g, '<em>$1</em>');
      
      // Handle text between parentheses for actions/narrative
      formattedContent = formattedContent.replace(/\(([^)]+)\)/g, '<span data-narrative>$1</span>');
    }
    
    return formattedContent;
  };

  // Handle three dots button click
  const handleDotsClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event from bubbling up
    
    if (isMobile) {
      // For mobile, show the mobile popup
      setShowMobilePopup(true);
    } else {
      // For desktop, show the popup at the mouse position
      // Add a small offset to position it better
      setPopupPosition({
        x: e.clientX + 5,  // Slight offset to the right
        y: e.clientY + 5   // Slight offset downward
      });
      
      // Show the popup
      setShowPopup(true);
    }
  };

  // Close popup when component unmounts
  useEffect(() => {
    return () => {
      setShowPopup(false);
      setShowMobilePopup(false);
    };
  }, []);

  // Handle popup actions
  const handleCopy = () => {
    // Copy message content to clipboard
    navigator.clipboard.writeText(message.content);
    setShowPopup(false);
    setShowMobilePopup(false);
    setIsSelected(false);
  };

  const handleNewChat = () => {
    console.log('New scenario from here', message.id);
    setShowPopup(false);
    setShowMobilePopup(false);
    setIsSelected(false);
    
    // Call the onNewScenarioFromMessage prop if it exists
    if (onNewScenarioFromMessage && message.id) {
      onNewScenarioFromMessage(message.id);
    }
  };

  const handleRewind = () => {
    console.log('Rewind to here', message.id);
    setShowPopup(false);
    setShowMobilePopup(false);
    setIsSelected(false);
    
    // Call the onRewindToMessage prop if it exists
    if (onRewindToMessage && message.id) {
      onRewindToMessage(message.id);
    }
  };

  // Handle edit message
  const handleEdit = () => {
    console.log('Edit message', message.id);
    setShowPopup(false);
    setShowMobilePopup(false);
    setIsSelected(false);
    
    // Enable editing mode based on device type
    if (isMobile) {
      setShowMobileEditForm(true);
    } else {
      setIsEditing(true);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setShowMobileEditForm(false);
  };

  // Handle save edited message
  const handleSaveEdit = async (newContent: string) => {
    if (onEditMessage && message.id) {
      try {
        await onEditMessage(message.id, newContent);
      } catch (error) {
        console.error('Failed to edit message:', error);
      }
    }
    
    // Exit editing mode
    setIsEditing(false);
    setShowMobileEditForm(false);
  };

  const handleRegenerate = () => {
    console.log('Regenerate message', message.id);
    setShowPopup(false);
    setShowMobilePopup(false);
    setIsSelected(false);
    
    // Call the onRegenerate prop if it exists
    if (onRegenerate && message.id) {
      onRegenerate(message.id);
    }
  };

  // Handle text animation complete
  const handleTextAnimationComplete = () => {
    if (onAnimationComplete) {
      onAnimationComplete();
    }
    setTextAnimationComplete(true);
  };

  // Get animation speed based on content length
  const getAnimationSpeed = (): number => {
    // Base speed
    const baseSpeed = 30; // characters per second
    
    // Apply speed adjustment based on message length
    const contentLength = message.content.length;
    if (contentLength > 500) return baseSpeed * 1.5;
    if (contentLength > 200) return baseSpeed * 1.2;
    return baseSpeed;
  };

  // Determine the appropriate CSS classes based on message type and state
  let containerClass = '';
  
  if (isNarrator) {
    containerClass = `${styles.narratorMessage} ${isMobile ? styles.mobileNarratorMessage : ''} ${isVisible ? styles.visible : ''} ${isRewinding ? styles.rewinding : ''} ${isRegenerating ? styles.regenerating : ''} ${isEditing ? styles.editing : ''} ${isInitialLoad ? styles.initialLoad : ''}`;
  } else {
    containerClass = `${styles.messageContainer} ${isUser ? styles.personaMessage : ''} ${isMobile ? styles.mobileMessage : ''} ${isVisible ? styles.visible : ''} ${isRewinding ? styles.rewinding : ''} ${isRegenerating ? styles.regenerating : ''} ${isEditing ? styles.editing : ''} ${isSelected ? styles.selected : ''} ${isInitialLoad ? styles.initialLoad : ''}`;
  }
  
  // Calculate the inline style for transition delay based on index
  const transitionDelayStyle = isInitialLoad ? { transitionDelay: `${index * 200}ms` } : {};
  
  // When regenerating, we want to keep the message visible but show the animation
  // Loading messages are only for new messages, not regeneration
  if (isLoading && !isRegenerating) {
    if (isNarrator) {
      return <NarratorLoadingMessage />;
    }
    return <LoadingMessage character={message.character} />;
  }

  if (isNarrator) {
    return (
      <>
        <div 
          id={`message-${message.id}`}
          className={containerClass}
          onClick={handleTap}
          style={transitionDelayStyle}
        >
          <div className={styles.narratorContent}>
            <AnimatedText
              content={formatContent(message.content)}
              isInitialLoad={isInitialLoad}
              isComplete={isUser ? true : textAnimationComplete}
              onComplete={handleTextAnimationComplete}
              htmlFormatted={true}
              speed={getAnimationSpeed()}
            />
          </div>
          
          {/* Show the three dots button on hover/tap */}
          {!isEditing && (
            <HoverDots 
              onButtonClick={handleDotsClick} 
              isMobile={isMobile} 
            />
          )}
          
          {/* Show popup menu when dots are clicked (desktop) */}
          {showPopup && (
            <ScenarioEditMessagePopUp 
              position={popupPosition}
              onClose={() => setShowPopup(false)}
              onCopy={handleCopy}
              onNewChat={handleNewChat}
              onRewind={handleRewind}
              onEdit={handleEdit}
              onRegenerate={handleRegenerate}
              isLatestMessage={isLatestMessage}
              isUserMessage={false}
            />
          )}
          
          {/* Show mobile popup when dots are clicked (mobile) */}
          {showMobilePopup && (
            <ScenarioEditMessagePopUpMobile
              onClose={() => setShowMobilePopup(false)}
              onCopy={handleCopy}
              onNewChat={handleNewChat}
              onRewind={handleRewind}
              onEdit={handleEdit}
              onRegenerate={handleRegenerate}
              isLatestMessage={isLatestMessage}
              isUserMessage={false}
            />
          )}
          
          {/* Show in-place edit interface when editing */}
          {isEditing && (
            <ScenarioInPlaceEdit
              content={message.content}
              onCancel={handleCancelEdit}
              onSave={handleSaveEdit}
              isUserMessage={false}
            />
          )}
        </div>

        {/* Mobile edit form popup */}
        {showMobileEditForm && (
          <ScenarioEditMessageFormMobile
            content={message.content}
            onSave={handleSaveEdit}
            onClose={handleCancelEdit}
            isVisible={showMobileEditForm}
          />
        )}
      </>
    );
  }

  // For mobile view, we need to handle the layout differently
  // but we no longer split the content into paragraphs
  const formattedContent = formatContent(message.content);

  return (
    <>
      <div 
        id={`message-${message.id}`}
        className={containerClass}
        onClick={handleTap}
        style={transitionDelayStyle}
      >
        {(!isMobile || isSelected) && <HoverDots isPersona={isUser} onButtonClick={handleDotsClick} isMobile={isMobile} />}
        {speaker && speaker.icon && (
          <div 
            className={styles.portraitContainer}
            onClick={(e) => {
              e.stopPropagation(); // Prevent event from bubbling up to the message container
              
              // Only character messages are clickable and navigate to character profiles
              if (message.character && message.character.username) {
                navigate(`/chars/${message.character.username}`);
              }
            }}
            style={{ 
              cursor: message.character?.username ? 'pointer' : 'default' 
            }}
          >
            <img src={speaker.icon} alt={speaker.display_name} className={styles.portrait} />
            <div className={styles.speakerName}>{speaker.display_name}</div>
          </div>
        )}
        <div className={styles.dialogueWrapper}>
          {isEditing && !isMobile ? (
            <ScenarioInPlaceEdit
              content={message.content}
              onCancel={handleCancelEdit}
              onSave={handleSaveEdit}
              isUserMessage={isUser}
            />
          ) : (
            <div className={`${styles.dialogueContent} ${isMobile ? styles.mobileDialogueContent : ''}`}>
              <AnimatedText
                content={formattedContent}
                isInitialLoad={isInitialLoad}
                isComplete={isUser ? true : textAnimationComplete}
                onComplete={handleTextAnimationComplete}
                htmlFormatted={true}
                speed={getAnimationSpeed()}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile edit form popup */}
      {showMobileEditForm && (
        <ScenarioEditMessageFormMobile
          content={message.content}
          onSave={handleSaveEdit}
          onClose={handleCancelEdit}
          isVisible={showMobileEditForm}
        />
      )}

      {/* Other popups */}
      {showPopup && (
        <ScenarioEditMessagePopUp
          position={popupPosition}
          onClose={() => setShowPopup(false)}
          onCopy={handleCopy}
          onNewChat={handleNewChat}
          onRewind={handleRewind}
          onEdit={handleEdit}
          onRegenerate={handleRegenerate}
          isLatestMessage={isLatestMessage}
          isUserMessage={isUser}
        />
      )}
      
      {showMobilePopup && (
        <ScenarioEditMessagePopUpMobile
          onClose={() => setShowMobilePopup(false)}
          onCopy={handleCopy}
          onNewChat={handleNewChat}
          onRewind={handleRewind}
          onEdit={handleEdit}
          onRegenerate={handleRegenerate}
          isLatestMessage={isLatestMessage}
          isUserMessage={isUser}
        />
      )}
    </>
  );
}

export default ScenarioMessage; 